import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Button,
  TextField,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  SelectChangeEvent,
  Tooltip
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import SearchBar from '../../Common/Search/SearchBar';
import { theme } from '../../../theme';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Pagination from '../../Common/Pagination/pagination';
import { TableDescription, TableRowStyled, TableWrapper } from '../JobManagement/JobListing/JobListing.styles';
import ProgressStepper, { StepData } from './ProgressStepper';
import { getPreboardingCandidates, sendPreboardingNotification } from './preboardingApi';
import { getEmailTemplates } from '../Admin/Communication/communicationApi';
import { emailTemplate } from '../CandidateManagement/CandidateContent/CandidateContent';
import Action from './Action';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { ArrowBack } from '@mui/icons-material';
import FilterPreboardingCandidates from './FilterPreboardingCandidates';
import { formatEpochToDate } from '../../Common/Date/ConvertDate';
import UpdateBGVStatus from './UpdateBGVStatus';
import { removeSpacing, sanitizeContent } from '../../Common/Sanitize/sanitizeReactQuill';
import { completeBgv } from './CandidatePreboarding/candidatePreboardingApi';
import { useLoadingBackdrop } from '../../../Context/LoadingBackdropContext';
import { updateCandidateEmail } from '../CandidateManagement/candidateManagementApi';
import { usePreboardingTabContext } from './PreboardingTabProvider';



export const statusStyles = {
  OfferPending: {
    label: "Offer Pending",
    color: "#E65100",
    backgroundColor: "#FFF6E5",
    border: "1px solid #FFB74D",
  },
  OfferSentAwaitingAcceptance: {
    label: "Awaiting Acknowledgment",
    color: "#795548",
    backgroundColor: "#FFF9C4",
    border: "1.5px solid #FDD835",
  },
  OfferDeclined: {
    label: "Offer Declined",
    color: "#C62828",
    backgroundColor: "#FDECEA",
    border: "1px solid #E57373",
  },
  OfferAccepted: {
    label: "Offer Accepted",
    color: "#388E3C",
    backgroundColor: "#E8F5E9",
    border: "1px solid #388E3C",
  },
  ProfileIncomplete: {
    label: "Profile Incomplete",
    color: "#1976D2",
    backgroundColor: "#E3F2FD",
    border: "1px solid #1976D2",
  },
  ProfileCompleted: {
    label: "Profile Completed",
    color: "#1565C0",
    backgroundColor: "#E3F2FD",
    border: "1px solid #64B5F6",
  },
  DocumentsPending: {
    label: "Documents Pending",
    color: "#6A1B9A",
    backgroundColor: "#F3E5F5",
    border: "1px solid #BA68C8",
  },
  DocumentsSubmitted: {
    label: "Documents Submitted",
    color: "#6A1B9A",
    backgroundColor: "#F3E5F5",
    border: "1px solid #6A1B9A",
  },
  PendingDocumentVerification: {
    label: "Verification in Progress",
    color: "#FF8F00",
    backgroundColor: "#F5F5DC",
    border: "1px solid #FFE082",
  },
  DocumentVerified: {
    label: "Document Verified",
    color: "#388E3C",
    backgroundColor: "#E8F5E9",
    border: "1px solid #388E3C",
  },
  BGVPending: {
    label: "BGV Pending",
    color: "#606400",
    backgroundColor: "#E0F7FA",
    border: "1px solid #4DD0E1",
  },
  BGVCleared: {
    label: "BGV Cleared",
    color: "#2E7D32",
    backgroundColor: "#E8F5E9",
    border: "1px solid #2E7D32",
  },
  Rejected: {
    label: "Rejected",
    color: "#C62828",
    backgroundColor: "#FFEBEE",
    border: "1px solid #C62828",
  },
  OnHold: {
    label: "On Hold",
    color: "#B54708",
    backgroundColor: "#FFFAEB",
    border: "1.5px solid #FEDF89",
  },
  Hired: {
    label: "Hired",
    color: "#1B5E20",
    backgroundColor: "#E8F5E9",
    border: "1px solid #66BB6A",
  },
  BGVRejected: {
    label: "BGV Rejected",
    color: "#D32F2F",
    backgroundColor: "#FFEBEE",
    border: "1px solid #EF5350",
  },
} as const;

type StatusKey = keyof typeof statusStyles;

export interface candidateType {
  CandidateEmail: string,
  CandidateName: string,
  CandidateNum: number,
  CandidateStatus: string,
  DeletedCandidate: boolean,
  DeletedItem: boolean,
  Department: string,
  JobNum: number,
  JobTitle: string,
  MapID: number,
  MatchedPct: string,
  PhoneNumber: string | null,
  PreboardingSortOrder: number | null,
  PreboardingStatus: string | null,
  PreboardingStatusCategory: string | null,
  PreboardingStatusId: number | null,
  PreboardingStatusKey: StatusKey | null,
  RecruiterName: string | null,
  PreboardingFlow: StepData[],
  WaitingOn: string,
  UpdatedDate: string | null,
  UpdatedBy: number | null,
  RecruiterEmail: string,
  Location: string,
  InternalTeamsNotification: {
    Id: number | null,
    IsSent: boolean,
    Team: string
  }[]
}

export interface PreboardingCandidatesFiltersType {
  recruiterName: string,
  department: string,
  filterByStatus?: string[],
  pendingWithFilter?: string | null,
  jobTitle?: string,
  location?: string
}




const AllCandidates = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const statusFromNav = location.state?.status;
  const [navigationFilterStatus, setNavigationFilterStatus] = useState<string | null>(statusFromNav || null);
  const columnsData = [
    'Candidate Name',
    'Status',
    'Preboarding Progress',
    'Assigned Recruiter',
    'Department',
    'Pending with',
    'Last Updated',
    'Action'
  ];
  const [loading, setLoading] = useState(true);
  const [candidatesData, setCandidatesData] = useState<candidateType[]>([]);
  const [emailData, setEmailData] = useState<emailTemplate[]>([])

  const [message, setMessage] = useState<string>('');
  const [status, setStatus] = useState<string | null>(null);
  const [subject, setSubject] = useState<string>('');

  const [candidateName, setCandidateName] = useState<string | null>(null);
  const [recruiterName, setRecruiterName] = useState<string | null>(null);
  const [candidatePosition, setCandidatePosition] = useState<string | null>(null);
  const [candidateEmail, setCandidateEmail] = useState<string | null>(null);
  const [preboardingStatus, setPreboardingStatus] = useState<string | null>(null);
  const [recruiterEmail, setRecruiterEmail] = useState<string | null>(null);
  const [candidateNum, setCandidateNum] = useState<number | null>(null);
  const [jobNum, setJobNum] = useState<number | null>(null);
  const [mapID, setMapID] = useState<number | null>(null);
  const [emailNum, setEmailNum] = useState<number | null>(null);
  const [hasBgvPendingCandidates, setHasBgvPendingCandidates] = useState(false)
  const { setShowFilter } = usePreboardingTabContext();
  const [wantHiringExperience, setWantHiringExperience] = useState(false);
  const [isRestartPreboarding, setIsRestartPreboarding] = useState(false);

  const [isUpdateBGVDialogOpen, setIsUpdateBGVDialogOpen] = useState(false);

  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const { showSnackbar } = useSnackbar();

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [preboardingCandidateFilters, setPreboardingCandidateFilters] = useState<PreboardingCandidatesFiltersType>({
    recruiterName: '',
    department: '',
    filterByStatus: [],
    pendingWithFilter: null
  });
  const [statusOptions, setStatusOptions] = useState<{ value: string, label: string }[]>([
    { value: 'OfferPending', label: 'Offer Pending' },
    { value: 'OfferSentAwaitingAcceptance', label: 'Awaiting Acknowledgment' },
    { value: 'OfferDeclined', label: 'Offer Declined' },
    { value: 'ProfileIncomplete', label: 'Profile Incomplete' },
    { value: 'DocumentsPending', label: 'Documents Pending' },
    { value: 'PendingDocumentVerification', label: 'Verification in Progress' },
    { value: 'BGVPending', label: 'BGV Pending' },
    { value: 'BGVRejected', label: 'BGV Rejected' },
    { value: 'Rejected', label: 'Rejected' },
    // { value: 'OnHold', label: 'On Hold' },
    { value: 'Hired', label: 'Hired' },
  ])
  const { show, hide } = useLoadingBackdrop();

  const handleCloseFilterDialog = () => {
    setIsFilterDialogOpen(false);
  };

  const handleCloseActionDialog = () => {
    setIsActionDialogOpen(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const hasActiveFilters = (filters: typeof preboardingCandidateFilters): boolean => {
    return (
      filters.recruiterName.trim() !== '' ||
      filters.department.trim() !== '' ||
      (filters.filterByStatus?.length ?? 0) > 0 ||
      filters.pendingWithFilter !== null
    );
  };

  const handleApplyFilters = (newFilters: any) => {
    const isFiltered = hasActiveFilters(newFilters);
    if (isFiltered) {
      setPreboardingCandidateFilters(newFilters)
      fetchPreboardingCandidates(newFilters, 1, 10, searchQuery, navigationFilterStatus)
    }

    handleCloseFilterDialog();
  }

  const handleClearFilterAPI = async () => {
    const clearedFilters = {
      recruiterName: '',
      department: '',
      filterByStatus: [],
      waitingOnFilter: null,
    };
    setPreboardingCandidateFilters(clearedFilters);
    fetchPreboardingCandidates(clearedFilters, 1, 10, searchQuery, navigationFilterStatus);
  }




  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setPage(1);
  };

  const fetchPreboardingCandidates = async (filters: PreboardingCandidatesFiltersType, page: number, rowsPerPage: number, searchQuery: string, navigationFilterStatus: string | null) => {
    setLoading(true)
    try {
      const payLoad = {
        page: page,
        rowsPerPage: rowsPerPage,
        CandidateName: searchQuery,
        RecruiterName: filters.recruiterName,
        Department: filters.department,
        PreboardingStatus: filters.filterByStatus,
        PendingWith: filters.pendingWithFilter,
        NavigationFilterStatus: navigationFilterStatus,
      }
      const response = await getPreboardingCandidates(tenant, payLoad)
      if (response) {
        setCandidatesData(response.Data)
        setTotalCandidates(response.Total)
        setHasBgvPendingCandidates(response.Data.filter((candidate: candidateType) => candidate.PreboardingStatusKey === "BGVPending").length > 0)

      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false);
    }
  }

  const fetchEmailTemplates = async () => {
    const result = await getEmailTemplates(tenant)
    setEmailData(result)
  }

  useEffect(() => {
    fetchEmailTemplates()
  }, [tenant]);

  useEffect(() => {
    fetchPreboardingCandidates(preboardingCandidateFilters, page, rowsPerPage, searchQuery, navigationFilterStatus);
  }, [page, rowsPerPage, tenant, navigationFilterStatus]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const selectedStatus = event.target.value;
    setStatus(selectedStatus);
    const matchedTemplate = emailData.find(
      (template) => template.EmailKey === selectedStatus
    );

    if (matchedTemplate) {
      setMessage(
        matchedTemplate.EmailBody
          .replaceAll('@CandidateName', candidateName ?? '')
          .replaceAll('@RecruiterEmail', recruiterEmail ?? '')
          .replaceAll('@Recruiter', recruiterName ?? '')
          .replaceAll('@JobTitle', candidatePosition ?? '')

      );
      setSubject(`${matchedTemplate.EmailSubject} Notification`);
      setEmailNum(matchedTemplate.EmailNum);
    } else {
      setMessage("No template found for this status.");
      setSubject("Default Notification");
      setEmailNum(null);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // 🚫 Skip on initial render
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const updatedFilters = {
        ...preboardingCandidateFilters,
      };
      fetchPreboardingCandidates(updatedFilters, page, rowsPerPage, searchQuery, navigationFilterStatus);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const handleActionClick = (name: string | null, email: string | null, jobTitle: string | null, recruiter: string | null, preboardingStatus: string | null, recruiterEmail: string | null, candidateNum: number | null, jobNum: number | null, mapID: number | null) => {
    setCandidateName(name);
    setCandidateEmail(email);
    setCandidatePosition(jobTitle);
    setRecruiterName(recruiter);
    setPreboardingStatus(preboardingStatus);
    setRecruiterEmail(recruiterEmail);
    setIsActionDialogOpen(true);
    setCandidateNum(candidateNum);
    setJobNum(jobNum);
    setMapID(mapID)
  }

  const handleSendNotification = async () => {
    if (!subject || !message || !candidateName || !candidateEmail || !recruiterEmail || !jobNum || !mapID || !status) {
      showSnackbar("Please select a status from the dropdown before sending the notification.", "error");
      return;
    }
    const sanitizedValue = sanitizeContent(message)
    if (!sanitizedValue) {
      showSnackbar("Message content cannot be empty.", "error");
      return;
    }
    if (!subject.trim()) {
      showSnackbar("Subject cannot be empty.", "error")
      return
    }
    try {
      const payLoad = {
        "EmailSubject": subject,
        "EmailBody": removeSpacing(message),
        "CandidateName": candidateName,
        "CandidateNum": candidateNum,
        "JobNum": jobNum,
        "MapID": mapID,
        "EmailID": candidateEmail,
        "EmailTemplateName": status,
        "EmailNum": emailNum,
        "IsHiringExperienceNeeded": wantHiringExperience,
        "IsRestartPreboarding": isRestartPreboarding
      }
      show()
      const result = await sendPreboardingNotification(tenant, payLoad)
      if (result) {
        showSnackbar("Notification sent successfully to the candidate.", "success");
        setIsActionDialogOpen(false);
        fetchPreboardingCandidates(preboardingCandidateFilters, page, rowsPerPage, searchQuery, navigationFilterStatus);
      }
    } catch (error) {
      showSnackbar("Failed to send notification to the candidate.", "error");
      setIsActionDialogOpen(false);
      console.log(error)
    } finally {
      hide()
    }
  }

  // Add this function inside your AllCandidates component
  const clearNavigationStatus = () => {
    setNavigationFilterStatus(null);
    setShowFilter('All Candidates');
    // Also clear from location.state if possible
    if (location.state && location.state.status) {
      navigate(location.pathname, { replace: true });
    }
  };

  const handleNavigationToCandidatePreboarding = (candidateName: string | null, candidateNum: number | null, jobNum: number | null, mapID: number | null, jobTitle: string | null) => {
    navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/candidate-offer`, {
      state: {
        candidateName: candidateName,
        jobNum: jobNum,
        mapID: mapID,
        candidateNum: candidateNum,
        jobTitle: jobTitle
      }
    });
  }

  const handleUpdateBgvStatus = async (payLoad: any) => {
    show();
    try {
      const response = await completeBgv(tenant, payLoad);
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error")
        if (response.Status) {
          fetchPreboardingCandidates(preboardingCandidateFilters, page, rowsPerPage, searchQuery, navigationFilterStatus);
        }
        return response.Status;
      }
      return false;
    } catch (error) {
      console.log(error)
    } finally {
      hide();
    }
    return false
  }

  const handleSaveEmail = async (payLoad: any) => {
    try {
      const response = await updateCandidateEmail(tenant, payLoad)
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error");
        if (response.Status) {
          setCandidatesData((prevList) =>
            prevList.map((candidate) =>
              candidate.CandidateNum === candidateNum
                ? { ...candidate, CandidateEmail: payLoad.CandidateEmail }
                : candidate
            )
          );
        }
        return response.Status
      }
      return false
    } catch (error) {
      showSnackbar("Failed to update email.", "error");
      console.log(error)
    }
    return false
  }

  return (
    <Box mt={2.5} width={'100%'}>
      <Box sx={{ display: 'flex', justifyContent: navigationFilterStatus ? navigationFilterStatus !== "TotalCandidates" ? 'space-between' : 'flex-end' : 'flex-end', mb: 2 }}>
        {
          navigationFilterStatus && navigationFilterStatus !== "TotalCandidates" && (
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={clearNavigationStatus}
            >
              Back to All Candidates
            </Button>
          )
        }
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder="Search by Candidate Name"
          />
          <Button variant="outlined" sx={{ color: theme.palette.primary.main, borderColor: theme.palette.primary.main }}
            onClick={() => setIsFilterDialogOpen(true)}
          >Filter & Sort</Button>

          {
            hasBgvPendingCandidates && (
              <Button
                variant="contained"
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  bgcolor: theme.palette.primary.main,
                }}
                onClick={() => setIsUpdateBGVDialogOpen(true)}
              >
                Update BGV Status
              </Button>
            )
          }

        </Box>
      </Box>


      <FilterPreboardingCandidates
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        onApply={handleApplyFilters}
        statusOptions={statusOptions}
        filters={preboardingCandidateFilters}
        handleClearFilterAPI={handleClearFilterAPI}
      />


      <UpdateBGVStatus
        isOpen={isUpdateBGVDialogOpen}
        onClose={() => setIsUpdateBGVDialogOpen(false)}
        handleUpdateBgvStatus={handleUpdateBgvStatus}
        emailData={emailData}
      />
      <TableWrapper >
        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {columnsData.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      color: theme.palette.text.secondary,
                      padding: '8px 12px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '13.89vw',
                      minWidth: '6.94vw',
                      textAlign: ['action', 'status'].includes(column.toLowerCase()) ? 'center' : 'left',
                    }}
                  >
                    <Typography variant='caption'>
                      {column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {
                loading ? (
                  <TableRow>
                    <TableCell colSpan={columnsData.length} style={{ textAlign: 'center' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : candidatesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columnsData.length} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      No Candidate Data Available
                    </TableCell>
                  </TableRow>
                ) : (
                  candidatesData.map((c, idx) => (
                    <TableRowStyled key={idx} iseven={(idx % 2 === 0).toString()}>
                      <TableDescription>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#e0e0e0' }}>
                            {c.CandidateName[0]}
                          </Avatar>
                          <Typography
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                            variant="body2"
                            onClick={() => handleNavigationToCandidatePreboarding(c.CandidateName, c.CandidateNum, c.JobNum, c.MapID, c.JobTitle)}
                          >
                            {c.CandidateName}
                          </Typography>
                        </Box>
                      </TableDescription>
                      <TableDescription
                        sx={{
                          paddingLeft: 0,
                          paddingRight: 0,
                          textAlign: 'center'
                        }}>
                        <Tooltip title={statusStyles[c.PreboardingStatusKey || 'OfferPending'].label}
                          slotProps={{
                            popper: {
                              sx: {
                                '& .MuiTooltip-tooltip': {
                                  bgcolor: 'white',
                                  color: 'black',
                                  fontWeight: 500,
                                  fontSize: '0.95em',
                                  boxShadow: 2,
                                }
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.2,
                              py: 0.3,
                              borderRadius: "1vw",
                              fontSize: "0.8vw",
                              fontWeight: 500,
                              width: '5vw',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              cursor: 'default',

                              ...statusStyles[c.PreboardingStatusKey || 'OfferPending']
                            }}>
                            {statusStyles[c.PreboardingStatusKey || 'OfferPending'].label}
                          </Box>
                        </Tooltip>


                      </TableDescription>
                      <TableDescription>
                        <ProgressStepper steps={c.PreboardingFlow} currentStep={statusStyles[c.PreboardingStatusKey || 'OfferPending'].label} />
                      </TableDescription>
                      <TableDescription>{c.RecruiterName}</TableDescription>
                      <TableDescription>{c.Department}</TableDescription>
                      <TableDescription>
                        {c.WaitingOn}
                      </TableDescription>
                      <TableDescription>
                        <Box>
                          <Typography variant="body2" >
                            {c.UpdatedBy ? c.UpdatedBy : '-'}
                          </Typography>
                          <Typography variant="body2">
                            {c.UpdatedDate ? formatEpochToDate(c.UpdatedDate) : '-'}
                          </Typography>
                        </Box>
                      </TableDescription>
                      <TableDescription sx={{
                        paddingLeft: 0,
                        paddingRight: 0,
                        textAlign: 'center'
                      }}>
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => {
                            handleActionClick(c.CandidateName, c.CandidateEmail, c.JobTitle, c.RecruiterName, c.PreboardingStatusKey, c.RecruiterEmail, c.CandidateNum, c.JobNum, c.MapID);
                          }}
                          sx={{ gap: 1 }}
                        >
                          <i className="fa-solid fa-list-check"></i>
                          Action
                        </Button>
                      </TableDescription>
                    </TableRowStyled>
                  ))
                )
              }

            </TableBody>
          </Table>
        </Box>

        {
          totalCandidates > 0 && (
            <Box sx={{ mt: '2.5vh', display: 'flex', mb: 2, mx: 2, justifyContent: 'center' }}>
              <Pagination
                totalRows={totalCandidates}
                rowsPerPage={rowsPerPage}
                currentPage={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          )
        }

      </TableWrapper>
      <Action
        isOpen={isActionDialogOpen}
        onClose={handleCloseActionDialog}
        email={candidateEmail}
        setEmail={setCandidateEmail}
        status={status}
        subject={subject}
        message={message}
        handleStatusChange={handleStatusChange}
        setMessage={setMessage}
        emailData={emailData}
        setSubject={setSubject}
        candidateName={candidateName}
        preboardingStatus={preboardingStatus}
        setStatus={setStatus}
        onSend={handleSendNotification}
        candidateNum={candidateNum}
        onEmailSave={handleSaveEmail}
        wantHiringExperience={wantHiringExperience}
        setWantHiringExperience={setWantHiringExperience}
        setIsRestartPreboarding={setIsRestartPreboarding}
      />
    </Box>
  );
};

export default AllCandidates;